<template>
  <div class="max-w-screen-2xl mx-auto relative">
    <TieredMenu
      ref="rowMenu"
      id="filing_country_row_actions_menu"
      :model="rowMenuItems"
      :popup="true"
      class="text-sm"
      :key="rowMenuRowSelected"
    />
    <!-- Loading Spinner -->
    <loading-spinner :is-loading="isLoading" />

    <div v-show="!isLoading">
      <!-- PageBreadcrumb Navigation -->
      <div class="my-4">
        <div class="flex max-md:flex-col gap-4">
          <PageBreadcrumb class="flex-grow" />
          <Button
            icon="pi pi-refresh"
            @click="refreshFilingCountries"
            :loading="isLoading"
            size="small"
            variant="outlined"
            label="Refresh Filing Country Data"
          />
        </div>
      </div>

      <DataTable
        stripedRows
        removableSort
        :value="filingCountries"
        v-model:filters="filters"
        filterDisplay="row"
        dataKey="id"
        paginator
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
        :rows="30"
        :rowsPerPageOptions="[10, 20, 30, 50]"
        class="shadow-md rounded-lg"
        scrollable
        :scrollHeight="'calc(100vh - 16rem)'"
      >
        <template #empty> No data found.</template>
        <template #loading> Loading data. Please wait.</template>
        <Column
          field="Name"
          header="Country Name"
          filter
          filterField="Name"
          sortable
          style="width: 15%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <span
              class="country-name"
              :class="data.Active ? 'font-semibold' : ''"
            >
              <span class="flag mr-2">{{
                getCountryFlagEmoji(data.CountryCode)
              }}</span>
              {{ data.Name }}
            </span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                id="nameFilter"
                v-model="filterModel.value"
                type="text"
                @input="filterCallback()"
                placeholder="Search by name"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="clearFilter('Name', filterCallback)"
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="CountryCode"
          header="Country Code"
          filter
          filterField="CountryCode"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          style="width: 12%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <div class="text-center">
              <span :class="data.Active ? 'font-bold' : ''">
                {{ data.CountryCode.toString().toUpperCase() }}
              </span>
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                id="countryCodeFilter"
                v-model="filterModel.value"
                type="text"
                @input="filterCallback()"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="clearFilter('CountryCode', filterCallback)"
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="CurrencyCode"
          header="Currency"
          filter
          filterField="CurrencyCode"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          style="width: 12%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <div
              v-if="hasPerms(PERMISSIONS.TAX_ADMIN_UPDATE)"
              class="flex items-center gap-2"
            >
              <Select
                :model-value="pendingCurrency(data)"
                @update:model-value="v => setPendingCurrency(data, v)"
                :options="currencyOptions"
                optionLabel="label"
                optionValue="value"
                filter
                autoFilterFocus
                filterPlaceholder="Search currencies"
                :placeholder="`${data.CurrencyCode.toString().toUpperCase()} (${getCurrencySymbol(data.CurrencyCode.toString().toUpperCase())})`"
                class="w-28 text-sm"
                :disabled="!!savingRow[data.CountryCode]"
                :data-test-id="`currency-select-${data.CountryCode}`"
              />
            </div>
            <div v-else class="text-center">
              <span>
                {{ data.CurrencyCode.toString().toUpperCase() }} ({{
                  getCurrencySymbol(data.CurrencyCode.toString().toUpperCase())
                }})
              </span>
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                id="currencyCodeFilter"
                v-model="filterModel.value"
                type="text"
                @input="filterCallback()"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="clearFilter('CurrencyCode', filterCallback)"
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="OandaDataSet.Description"
          header="Oanda Source"
          filter
          filterField="OandaDataSet.Description"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <div
              v-if="hasPerms(PERMISSIONS.TAX_ADMIN_UPDATE)"
              class="flex items-center gap-2"
            >
              <Select
                :model-value="pendingOanda(data)"
                @update:model-value="v => setPendingOanda(data, v)"
                :options="oandaOptions"
                optionLabel="label"
                optionValue="value"
                filter
                autoFilterFocus
                filterPlaceholder="Search data sources"
                :placeholder="`${data.OandaDataSet.OandaCode.toString().toUpperCase()}`"
                class="w-44 text-sm"
                :disabled="!!savingRow[data.CountryCode]"
                :data-test-id="`oanda-select-${data.CountryCode}`"
              />
            </div>
            <div v-else class="text-center cursor-pointer">
              <div
                class="text-sm font-semibold text-surface-700 dark:text-surface-200"
                v-tooltip.top="{
                  value: data.OandaDataSet.Description.toString().toUpperCase(),
                }"
              >
                {{ data.OandaDataSet.OandaCode.toString().toUpperCase() }}
                <i class="pi pi-info-circle text-xs"></i>
              </div>
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                id="oandaDataSetFilter"
                v-model="filterModel.value"
                type="text"
                @input="filterCallback()"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="clearFilter('OandaDataSet.Description', filterCallback)"
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          field="DeMinimis"
          header="De Minimis"
          filter
          filterField="DeMinimis.Threshold"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          style="width: 12%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <div class="text-center">
              <div v-if="data.DeMinimis" class="text-sm flex flex-col gap-1">
                <div class="font-normal text-xs">
                  {{ data.DeMinimis.Algorithm.replace('_', ' ') }}:
                </div>
                <div class="text-sm text-primary font-semibold">
                  {{
                    getCurrencySymbol(
                      data.CurrencyCode.toString().toUpperCase(),
                    )
                  }}
                  {{ data.DeMinimis.Threshold }}
                </div>
              </div>
              <div
                v-else
                class="text-surface-400 dark:text-surface-500 text-xs text-center"
              >
                N/A
              </div>
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <IconField>
              <InputText
                id="deMinimisFilter"
                v-model="filterModel.value"
                type="text"
                @input="filterCallback()"
                class="p-column-filter w-full"
              />
              <InputIcon
                @click="clearFilter('DeMinimis.Threshold', filterCallback)"
                class="pi pi-times"
              />
            </IconField>
          </template>
        </Column>
        <Column
          header="Templates"
          filter
          filterField="TemplateTypes"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          style="width: 11%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
          v-if="hasPerms(PERMISSIONS.TAX_ADMIN_UPDATE)"
        >
          <template #body="{ data }">
            <div class="flex justify-center">
              <MultiSelect
                :model-value="pendingTemplates(data)"
                @update:model-value="v => setPendingTemplates(data, v)"
                :options="templateOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Templates"
                class="template-multiselect w-full"
                :data-test-id="`template-select-${data.CountryCode}`"
                :filter="false"
                :showToggleAll="false"
                :disabled="!!savingRow[data.CountryCode]"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <div class="flex items-center">
              <MultiSelect
                v-model="filterModel.value"
                :options="templateOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Filter by templates"
                :maxSelectedLabels="2"
                class="p-column-filter w-full"
                @change="filterCallback()"
              />
              <Button
                icon="pi pi-times"
                text
                size="small"
                class="ml-1"
                @click="clearTemplateFilter(filterCallback)"
                v-if="filterModel.value && filterModel.value.length > 0"
              />
            </div>
          </template>
        </Column>
        <Column
          field="Active"
          header="Active"
          filter
          filterField="Active"
          sortable
          :showFilterMatchModes="false"
          :showFilterOperator="false"
          :showFilterMenu="false"
          style="width: 10%"
          class="text-sm font-medium text-surface-600 dark:text-surface-300"
        >
          <template #body="{ data }">
            <div class="text-center flex items-center gap-2">
              <span
                :class="[
                  data.Active
                    ? 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                    : 'text-red-500 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
                  'p-1.5 rounded-lg text-xs min-w-16 text-center',
                ]"
              >
                {{ data.Active ? 'ACTIVE' : 'INACTIVE' }}
              </span>

              <ToggleSwitch
                id="activeToggle"
                v-if="hasPerms(PERMISSIONS.TAX_ADMIN_UPDATE)"
                v-model="data.Active"
                readonly
                @click="promptChangeActive(data)"
              />
            </div>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <Select
              v-model="filterModel.value"
              @change="filterCallback()"
              :options="[
                { label: 'True', value: true },
                { label: 'False', value: false },
                { label: 'All', value: null },
              ]"
              optionLabel="label"
              optionValue="value"
              class="p-column-filter w-full"
            />
          </template>
        </Column>
        <!-- Actions Column -->
        <Column
          header="Actions"
          style="width: 4rem"
          class="text-sm font-medium text-surface-600 dark:text-surface-300 border-l border-surface-200 dark:border-surface-700 text-center"
          frozen
          alignFrozen="right"
          data-test-id="filing-country-actions-column"
        >
          <template #body="{ data }">
            <div class="flex justify-center items-center gap-1">
              <!-- Per-row Save button: visible only when there are pending changes -->
              <Button
                v-if="hasPerms(PERMISSIONS.TAX_ADMIN_UPDATE) && hasPendingChanges(data)"
                icon="pi pi-save"
                rounded
                size="small"
                aria-label="Save Changes"
                :loading="!!savingRow[data.CountryCode]"
                :disabled="!!savingRow[data.CountryCode]"
                :data-test-id="`row-save-${data.CountryCode}`"
                @click="openSaveDialog(data)"
              />
              <Button
                icon="pi pi-ellipsis-v"
                rounded
                variant="outlined"
                aria-label="Row Actions"
                @click.prevent="toggleRowMenu($event, data)"
                :data-test-id="`filing-country-actions-btn-${data.CountryCode}`"
              />
            </div>
          </template>
        </Column>
      </DataTable>

      <!-- Existing change active dialog -->
      <Dialog
        v-model:visible="changeActivePrompt"
        modal
        header="Confirm Update"
        :closable="false"
      >
        <p>
          Are you sure you want to update the status of
          <strong>{{ selectedFilingCountry?.Name }}</strong
          >?
        </p>
        <template #footer>
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="cancelChangeActivePrompt"
          />
          <Button
            label="Yes"
            icon="pi pi-check"
            class="p-button-primary"
            :loading="isChangingActive"
            @click="confirmChangeActive"
          />
        </template>
      </Dialog>

      <!-- Per-row review dialog -->
      <FilingCountryRowSaveDialog
        v-if="dialogCountry"
        v-model:visible="dialogVisible"
        :countryName="dialogCountry.Name"
        :changes="dialogChanges"
        :submitting="dialogSubmitting"
        @confirm="confirmRowSave"
        @cancel="cancelRowSave"
      />

      <!-- Audit Info Dialog -->
      <AuditInfoDialog
        v-model="auditDialogVisible"
        :subject="auditDialogSubject"
        title="Filing Country Audit Info"
        :label="
          auditDialogSubject
            ? `${auditDialogSubject.Name} (${auditDialogSubject.CountryCode})`
            : ''
        "
        data-test-id="filing-country-audit-info-dialog"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import FilingCountryRowSaveDialog from '@/components/filingcountry/FilingCountryRowSaveDialog.vue'
import type { ChangeEntry } from '@/components/filingcountry/FilingCountryRowSaveDialog.vue'
import { FilterMatchMode } from '@primevue/core/api'
import { filingCountryConfigApi } from '@/services/filingCountryConfigService'
import {
  Button,
  Column,
  DataTable,
  Dialog,
  IconField,
  InputIcon,
  InputText,
  Select,
  ToggleSwitch,
  MultiSelect,
  useToast,
  TieredMenu,
} from 'primevue'
import Tooltip from 'primevue/tooltip'
import { onBeforeMount, ref, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useFrontegg } from '@frontegg/vue'
import { errorToast, errorToastWithDetails, successToast } from '@/util/toasts'
import { buildCurrencyOptions, getCountryFlagEmoji, getCurrencySymbol } from '@/util/util.js'
import type {
  CurrencyCatalogEntry,
  FilingCountry,
  FilingCountryPatchRequest,
  OandaDataSet,
} from '@/types/filingCountry'
import { normalizeError } from '@/util/errorNormalizer'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'
import { useFilingCountryStore } from '@/stores/filingCountryStore.ts'
import AuditInfoDialog from '@/components/common/AuditInfoDialog.vue'

const { authState } = useFrontegg()
const toast = useToast()
const router = useRouter()
const { hasPerms } = usePerms()

// Make the directive available
const vTooltip = Tooltip

const rowMenu = ref(null)
const rowMenuItems = ref<any[]>([])
const rowMenuRowSelected = ref<PropertyKey | undefined>(undefined) ?? undefined

const filters = ref<Record<string, { value: any; matchMode: string }>>({
  Name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  CountryCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
  CurrencyCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
  'OandaDataSet.Description': {
    value: null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  'DeMinimis.Threshold': {
    value: null,
    matchMode: FilterMatchMode.CONTAINS,
  },
  Active: { value: null, matchMode: FilterMatchMode.EQUALS },
  TemplateTypes: { value: null, matchMode: FilterMatchMode.CONTAINS },
})

const isLoading = ref(true)
const selectedFilingCountry = ref<FilingCountry | null>(null)
const changeActivePrompt = ref(false)
const isChangingActive = ref(false)

// Audit dialog state
const auditDialogVisible = ref(false)
const auditDialogSubject = ref<FilingCountry | null>(null)

const filingCountryStore = useFilingCountryStore()

const templateOptions = ref([
  { label: 'Digital', value: 'DIGITAL' },
  { label: 'Physical', value: 'PHYSICAL' },
  { label: 'Service', value: 'SERVICE' },
])

// ──────────────────────────────────────────────────────────────────────────────
// Pending-changes model (per-row, per-field)
// ──────────────────────────────────────────────────────────────────────────────
interface PendingRow {
  currency?: string
  oanda?: string
  templates?: string[]
}

/**
 * Keyed by CountryCode. Each entry holds the user's local (unsaved) edits for
 * that row. Only fields that differ from the persisted value are stored; a field
 * is cleared when the user re-selects the original value.
 */
const pendingChanges = ref<Record<string, PendingRow>>({})

/** Per-row saving flag for double-click prevention. */
const savingRow = ref<Record<string, boolean>>({})

// ── Pending field helpers ─────────────────────────────────────────────────────

function pendingCurrency(country: FilingCountry): string | undefined {
  return pendingChanges.value[country.CountryCode]?.currency
}

function setPendingCurrency(country: FilingCountry, value: string) {
  const code = country.CountryCode
  const persisted = country.CurrencyCode.toString().toUpperCase()
  if (!pendingChanges.value[code]) {
    pendingChanges.value[code] = {}
  }
  if (value === persisted) {
    delete pendingChanges.value[code].currency
  } else {
    pendingChanges.value[code].currency = value
  }
}

function pendingOanda(country: FilingCountry): string | undefined {
  return pendingChanges.value[country.CountryCode]?.oanda
}

function setPendingOanda(country: FilingCountry, value: string) {
  const code = country.CountryCode
  const persisted = country.OandaDataSet?.OandaCode?.toString().toUpperCase()
  if (!pendingChanges.value[code]) {
    pendingChanges.value[code] = {}
  }
  if (value === persisted) {
    delete pendingChanges.value[code].oanda
  } else {
    pendingChanges.value[code].oanda = value
  }
}

// helper to put template types into an array from the boolean fields for use in the multiselect
function getTemplateTypes(country: FilingCountry): string[] {
  const types: string[] = []
  if (country.IncludedInPhysicalGoodsTemplate) types.push('PHYSICAL')
  if (country.IncludedInDigitalGoodsTemplate) types.push('DIGITAL')
  if (country.IncludedInServicesTemplate) types.push('SERVICE')
  return types
}

function pendingTemplates(country: FilingCountry): string[] {
  const pending = pendingChanges.value[country.CountryCode]?.templates
  return pending !== undefined ? pending : getTemplateTypes(country)
}

function setPendingTemplates(country: FilingCountry, value: string[]) {
  const code = country.CountryCode
  const persisted = getTemplateTypes(country)
  if (!pendingChanges.value[code]) {
    pendingChanges.value[code] = {}
  }
  // Check whether the new value differs from persisted
  const same =
    value.length === persisted.length &&
    value.every(v => persisted.includes(v))
  if (same) {
    delete pendingChanges.value[code].templates
  } else {
    pendingChanges.value[code].templates = value
  }
}

/**
 * Returns true if the row has at least one pending change that differs from
 * the persisted value.
 */
function hasPendingChanges(country: FilingCountry): boolean {
  const pending = pendingChanges.value[country.CountryCode]
  if (!pending) return false
  return (
    pending.currency !== undefined ||
    pending.oanda !== undefined ||
    pending.templates !== undefined
  )
}

// ── Review dialog state ───────────────────────────────────────────────────────

const dialogVisible = ref(false)
const dialogCountry = ref<FilingCountry | null>(null)
const dialogChanges = ref<ChangeEntry[]>([])
const dialogSubmitting = ref(false)

function buildChangeEntries(country: FilingCountry): ChangeEntry[] {
  const pending = pendingChanges.value[country.CountryCode]
  if (!pending) return []
  const entries: ChangeEntry[] = []

  if (pending.currency !== undefined) {
    const from = `${country.CurrencyCode.toString().toUpperCase()} (${getCurrencySymbol(country.CurrencyCode.toString().toUpperCase())})`
    const to = `${pending.currency} (${getCurrencySymbol(pending.currency)})`
    entries.push({ field: 'Currency', from, to })
  }

  if (pending.oanda !== undefined) {
    entries.push({
      field: 'OANDA Source',
      from: country.OandaDataSet?.OandaCode?.toString().toUpperCase() ?? '',
      to: pending.oanda,
    })
  }

  if (pending.templates !== undefined) {
    const templateLabel = (codes: string[]) =>
      codes.length === 0
        ? 'None'
        : codes.map(c => c.charAt(0) + c.slice(1).toLowerCase()).join(', ')
    entries.push({
      field: 'Templates',
      from: templateLabel(getTemplateTypes(country)),
      to: templateLabel(pending.templates),
    })
  }

  return entries
}

function openSaveDialog(country: FilingCountry) {
  dialogCountry.value = country
  dialogChanges.value = buildChangeEntries(country)
  dialogVisible.value = true
}

function cancelRowSave() {
  // Close dialog without discarding pending edits
  dialogVisible.value = false
  dialogCountry.value = null
  dialogChanges.value = []
}

async function confirmRowSave() {
  const country = dialogCountry.value
  if (!country) return

  const code = country.CountryCode
  const pending = pendingChanges.value[code]
  if (!pending) return

  // Atomic double-click prevention (CLAUDE.md pattern)
  const wasAlreadySaving = dialogSubmitting.value
  dialogSubmitting.value = true
  if (wasAlreadySaving) return

  savingRow.value[code] = true

  // Build combined PATCH body containing only changed fields
  const patchBody: FilingCountryPatchRequest = {}
  if (pending.currency !== undefined) {
    patchBody.CurrencyCode = pending.currency
  }
  if (pending.oanda !== undefined) {
    patchBody.OandaDataSet = pending.oanda
  }
  if (pending.templates !== undefined) {
    patchBody.IncludedInPhysicalGoodsTemplate = pending.templates.includes('PHYSICAL')
    patchBody.IncludedInDigitalGoodsTemplate = pending.templates.includes('DIGITAL')
    patchBody.IncludedInServicesTemplate = pending.templates.includes('SERVICE')
  }

  try {
    const updatedCountry = await filingCountryConfigApi.patchFilingCountry(
      authState,
      code,
      patchBody,
    )
    filingCountryStore.updateFilingCountry(updatedCountry)
    // Clear pending state for this row
    delete pendingChanges.value[code]
    successToast(toast, 'Changes saved', `for ${country.Name}`)
    dialogVisible.value = false
    dialogCountry.value = null
    dialogChanges.value = []
  } catch (error) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Failed to save changes', apiError.toToastMessage(), apiError)
    // Keep pending state so user can retry; close dialog so they can continue editing
    dialogVisible.value = false
    dialogCountry.value = null
    dialogChanges.value = []
  } finally {
    dialogSubmitting.value = false
    savingRow.value[code] = false
  }
}

// ── Catalog fetching ──────────────────────────────────────────────────────────

// Currency catalog — fetched on mount when user has TAX_ADMIN_UPDATE
const currencyCatalog = ref<CurrencyCatalogEntry[]>([])

// Currency options: sourced from the backend catalog when available,
// falling back to the loaded filing countries if the catalog fetch fails.
// USD and EUR are pinned to the top of the list.
const currencyOptions = computed(() => {
  if (currencyCatalog.value.length > 0) {
    return buildCurrencyOptions(currencyCatalog.value)
  }
  return buildCurrencyOptions(filingCountryStore.getFilingCountries())
})

// OANDA catalog — fetched on mount when user has TAX_ADMIN_UPDATE
const oandaCatalog = ref<OandaDataSet[]>([])

// OANDA options: { label: 'CACB — Bank of Canada', value: 'CACB' }
// Falls back to the distinct OandaCode values from loaded filing countries
// if the catalog has not yet been fetched (avoids an empty dropdown).
const oandaOptions = computed(() => {
  if (oandaCatalog.value.length > 0) {
    return oandaCatalog.value.map(entry => ({
      label: `${entry.OandaCode} — ${entry.Description}`,
      value: entry.OandaCode,
    }))
  }
  // Fallback: build from distinct OandaDataSet entries in loaded countries
  const seen = new Set<string>()
  const opts: { label: string; value: string }[] = []
  for (const country of filingCountryStore.getFilingCountries()) {
    const code = country.OandaDataSet?.OandaCode
    const desc = country.OandaDataSet?.Description ?? ''
    if (code && !seen.has(code)) {
      seen.add(code)
      opts.push({ label: `${code} — ${desc}`, value: code })
    }
  }
  return opts.sort((a, b) => a.value.localeCompare(b.value))
})

// ── Filing countries computed ─────────────────────────────────────────────────

const filingCountries = computed(() => {
  return filingCountryStore
    .getFilingCountries()
    .map((country: FilingCountry) => ({
      ...country,
      TemplateTypes: getTemplateTypes(country),
    }))
    .sort((a: FilingCountry, b: FilingCountry) => {
      if (a.Active && !b.Active) return -1
      if (!a.Active && b.Active) return 1
      return a.Name.localeCompare(b.Name)
    })
})

const refreshFilingCountries = async () => {
  isLoading.value = true
  try {
    await filingCountryStore.fetchFilingCountries(authState)
    isLoading.value = false
  } catch (error) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    isLoading.value = false
  }
}

onBeforeMount(() => {
  if (!hasPerms(PERMISSIONS.TAX_CONFIG_READ)) return

  // Fetch catalogs for the editor selects (same read perm as filing countries).
  // Errors are non-blocking: selects fall back to distinct values from loaded
  // countries so they remain functional even if catalog endpoints are down.
  filingCountryConfigApi
    .getCurrencies(authState)
    .then(data => {
      currencyCatalog.value = data
    })
    .catch((error: unknown) => {
      const apiError = normalizeError(error)
      errorToast(toast, 'Failed to load currency catalog', apiError.toToastMessage())
    })

  filingCountryConfigApi
    .getOandaDataSets(authState)
    .then(data => {
      oandaCatalog.value = data
    })
    .catch((error: unknown) => {
      const apiError = normalizeError(error)
      errorToast(toast, 'Failed to load Oanda catalog', apiError.toToastMessage())
    })

  // Fetch only if filing countries are not populated
  if (filingCountryStore.getFilingCountries().length > 0) {
    isLoading.value = false
    return
  }
  filingCountryStore
    .fetchFilingCountries(authState)
    .then(() => {
      isLoading.value = false
    })
    .catch((error: unknown) => {
      const apiError = normalizeError(error)
      errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
      isLoading.value = false
    })
})

const clearFilter = (field: string, callback: Function) => {
  filters.value[field].value = null
  nextTick(() => {
    if (callback) callback()
  })
}

const clearTemplateFilter = (callback: Function) => {
  filters.value.TemplateTypes.value = null
  nextTick(() => {
    if (callback) callback()
  })
}

const promptChangeActive = (data: FilingCountry) => {
  selectedFilingCountry.value = { ...data }
  changeActivePrompt.value = true
}

const cancelChangeActivePrompt = () => {
  changeActivePrompt.value = false
  selectedFilingCountry.value = null
}

const confirmChangeActive = async () => {
  if (isChangingActive.value) return

  if (!selectedFilingCountry.value) return

  try {
    isChangingActive.value = true
    selectedFilingCountry.value.Active = !selectedFilingCountry.value.Active
    await filingCountryConfigApi.updateFilingCountry(
      authState,
      selectedFilingCountry.value,
    )
    successToast(toast, 'Success', 'Filing country updated successfully')
    const updatedCountry = selectedFilingCountry.value as FilingCountry
    changeActivePrompt.value = false
    selectedFilingCountry.value = null
    filingCountryStore.updateFilingCountry(updatedCountry)
  } catch (error: any) {
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    changeActivePrompt.value = false
  } finally {
    isChangingActive.value = false
  }
}

const navigateToCountryConfig = (countryCode: string) => {
  router.push({
    name: 'filingCountryConfiguration',
    params: { countryCode },
  })
}

async function toggleRowMenu(event: Event, rowData: FilingCountry) {
  rowMenuRowSelected.value = rowData.CountryCode ?? undefined
  rowMenuItems.value = buildRowMenuItems(rowData)
  await nextTick()
  if (rowMenu.value) {
    ;(rowMenu.value as any).toggle(event)
  }
}

function buildRowMenuItems(rowData: FilingCountry) {
  const items: any[] = []

  items.push({
    label: 'Configure Country',
    icon: 'pi pi-cog',
    command: () => navigateToCountryConfig(rowData.CountryCode),
  })

  if (hasAudit(rowData)) {
    items.push({
      label: 'View Audit Info',
      icon: 'pi pi-clock',
      command: () => openAuditModal(rowData),
    })
  }

  return items
}

function openAuditModal(row: FilingCountry) {
  auditDialogSubject.value = row
  auditDialogVisible.value = true
}

function hasAudit(item: FilingCountry) {
  const a = item?.Audit
  if (!a) return false
  return !!(a.CreatedBy || a.CreatedOn || a.LastModifiedBy || a.LastModifiedOn)
}
</script>

<style scoped></style>
